import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { apiDeleteAccount } from "../../services/authService";
import "../../App.css";

export default function DeleteAccountModal({ isOpen, onClose, user }) {
  const { signIn, signOut } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("confirm"); // "confirm" | "reauth"
  const [usernameInput, setUsernameInput] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const firstInputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setStep("confirm");
      setUsernameInput("");
      setPassword("");
      setError("");
      setLoading(false);
      setTimeout(() => firstInputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    function handleKey(e) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  async function finishDeletion() {
    await signOut();
    navigate("/");
  }

  async function attemptDelete() {
    const result = await apiDeleteAccount();
    if (result.ok) {
      await finishDeletion();
      return;
    }
    if (result.reauthRequired) {
      setStep("reauth");
      return;
    }
  }

  async function handleConfirmSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await attemptDelete();
    } catch (err) {
      setError(err.message || "Could not delete account.");
    } finally {
      setLoading(false);
    }
  }

  async function handleReauthSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await signIn(user.email, password);
      // Fresh token in hand — retry the deletion automatically, no second click.
      await attemptDelete();
    } catch (err) {
      setError(err.message || "Incorrect password. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  const confirmDisabled = loading || usernameInput !== user.username;

  return (
    <div className="auth-overlay" onClick={onClose} role="dialog" aria-modal="true" aria-labelledby="delete-modal-title">
      <div className="auth-modal delete-modal" onClick={(e) => e.stopPropagation()}>
        <button className="auth-close" onClick={onClose} aria-label="Close">✕</button>

        {step === "confirm" ? (
          <>
            <h2 className="auth-title" id="delete-modal-title">Delete Account</h2>
            <p className="delete-modal-warning">
              This will permanently delete your profile and battle history and remove your account access.
              This cannot be undone. Battles you've searched remain as shared results — they are not deleted.
            </p>
            <form className="auth-form" onSubmit={handleConfirmSubmit} noValidate>
              <div className="auth-field">
                <label className="auth-label" htmlFor="delete-username-confirm">
                  Type <strong>{user.username}</strong> to confirm
                </label>
                <input
                  id="delete-username-confirm"
                  ref={firstInputRef}
                  className="auth-input"
                  type="text"
                  value={usernameInput}
                  onChange={(e) => setUsernameInput(e.target.value)}
                  autoComplete="off"
                />
              </div>
              {error && <p className="auth-error">{error}</p>}
              <button className="danger-btn" type="submit" disabled={confirmDisabled}>
                {loading ? "Deleting…" : "Delete Account"}
              </button>
            </form>
          </>
        ) : (
          <>
            <h2 className="auth-title" id="delete-modal-title">Confirm It's You</h2>
            <p className="delete-modal-warning">
              For your security, please re-enter your password to continue.
            </p>
            <form className="auth-form" onSubmit={handleReauthSubmit} noValidate>
              <div className="auth-field">
                <label className="auth-label" htmlFor="delete-reauth-password">Password</label>
                <input
                  id="delete-reauth-password"
                  className="auth-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  autoFocus
                />
              </div>
              {error && <p className="auth-error">{error}</p>}
              <button className="danger-btn" type="submit" disabled={loading || !password}>
                {loading ? "Confirming…" : "Confirm"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
